import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Lightbulb,
  ListChecks,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import {
  predictions as predictionsApi,
  signals as signalsApi,
  entities as entitiesApi,
  type ApiSignal,
  type PredictionRunResult,
} from "@/lib/api";

export const Route = createFileRoute("/$workspace/hypotheses")({
  head: () => ({ meta: [{ title: "Hypotheses — Dravian Horizon" }] }),
  component: HypothesesPage,
});

function HypothesesPage() {
  const { workspace: id } = useParams({ from: "/$workspace/hypotheses" });
  const qc = useQueryClient();
  const [lastRun, setLastRun] = useState<PredictionRunResult | null>(null);

  const sigs = useQuery({
    queryKey: ["signals", id],
    queryFn: () => signalsApi.list(id),
  });
  const ents = useQuery({
    queryKey: ["entities", id],
    queryFn: () => entitiesApi.list(id),
  });
  const attempts = useQuery({
    queryKey: ["prediction_attempts", id],
    queryFn: () => predictionsApi.attempts(id),
  });

  const runMut = useMutation({
    mutationFn: () => predictionsApi.run(id),
    onSuccess: (r) => {
      setLastRun(r);
      qc.invalidateQueries({ queryKey: ["signals", id] });
      qc.invalidateQueries({ queryKey: ["prediction_attempts", id] });
    },
  });

  const hypotheses = useMemo(
    () =>
      (sigs.data ?? [])
        .filter((s) => s.type === "prediction")
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [sigs.data],
  );
  const entityNameById = useMemo(
    () => Object.fromEntries((ents.data ?? []).map((e) => [e.id, e.name])),
    [ents.data],
  );

  return (
    <div className="p-5 max-w-[1100px] mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow mb-1">Hypotheses worth checking</div>
          <h2 className="text-[20px] font-semibold">What the system thinks you might be missing</h2>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-[700px]">
            Pattern detectors find non-obvious shapes in your data (temporal spikes, co-occurrences, gaps, geographic spread). Your configured AI model turns those into anchored hypotheses with explicit evidence and falsification criteria. These are <strong>not predictions</strong> — they're flagged for your review. The system rejects ungrounded or vague output silently and surfaces only what passes the gates.
          </p>
        </div>
        <button
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[12px] font-semibold disabled:opacity-60 shrink-0"
        >
          <Sparkles className="h-4 w-4" />
          {runMut.isPending ? "Analyzing…" : "Generate hypotheses"}
        </button>
      </div>

      {lastRun && (
        <div className="rounded-md border border-primary/40 bg-primary/5 px-4 py-3 text-[12px]">
          <div className="flex items-center gap-2 text-foreground/90">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span className="font-semibold">Run complete</span>
          </div>
          <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
            <Stat label="Patterns detected" value={lastRun.patterns_detected} />
            <Stat label="Hypotheses proposed" value={lastRun.hypotheses_proposed} />
            <Stat label="Accepted" value={lastRun.hypotheses_accepted} tone="good" />
            <Stat label="Rejected by gates" value={lastRun.hypotheses_rejected} tone="bad" />
          </div>
          {Object.keys(lastRun.rejection_summary).length > 0 && (
            <div className="mt-2 text-[10px] text-muted-foreground">
              Rejection reasons:{" "}
              {Object.entries(lastRun.rejection_summary)
                .map(([k, v]) => `${k}×${v}`)
                .join(" · ")}
            </div>
          )}
        </div>
      )}

      {runMut.isError && (
        <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {String(runMut.error)}
        </div>
      )}

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="h-4 w-4 text-primary" />
          <Eyebrow count={hypotheses.length}>Active hypotheses</Eyebrow>
        </div>
        {hypotheses.length === 0 ? (
          <div className="panel p-6 text-center text-[12px] text-muted-foreground">
            No hypotheses yet. Run the generator above — it will only emit anchored, non-obvious
            hypotheses, or zero if no patterns warrant attention.
          </div>
        ) : (
          <div className="space-y-3">
            {hypotheses.map((h) => (
              <HypothesisCard
                key={h.id}
                workspaceId={id}
                signal={h}
                entityNameById={entityNameById}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="h-4 w-4 text-muted-foreground" />
          <Eyebrow count={attempts.data?.length ?? 0}>Rejected for tuning</Eyebrow>
        </div>
        <RejectedAttempts attempts={attempts.data ?? []} />
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "bad";
}) {
  const color =
    tone === "good" ? "text-risk-low" : tone === "bad" ? "text-risk-medium" : "text-foreground/90";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-[20px] font-mono font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function HypothesisCard({
  workspaceId,
  signal,
  entityNameById,
}: {
  workspaceId: string;
  signal: ApiSignal;
  entityNameById: Record<string, string>;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [promoted, setPromoted] = useState<string | null>(null);

  const promoteMut = useMutation({
    mutationFn: () => predictionsApi.promoteToScenario(workspaceId, signal.id),
    onSuccess: (s) => setPromoted(s.id),
  });

  const a = signal.attributes as Record<string, unknown>;
  const claim = String(a.claim ?? "");
  const reasoning = String(a.reasoning ?? "");
  const falsification = String(a.falsification_criteria ?? "");
  const novelty = String(a.novelty_explanation ?? "");
  const confidence = Number(a.confidence ?? 0);
  const horizon = Number(a.time_horizon_days ?? 0);
  const involved = (a.involved_entity_names ?? []) as string[];
  const evidenceSignals = (a.evidence_signal_ids ?? []) as string[];
  const evidenceEntities = signal.related_entity_ids;

  const confColor =
    confidence < 0.3 ? "text-muted-foreground" : confidence < 0.55 ? "text-primary" : "text-risk-medium";

  return (
    <div className="panel p-4 space-y-3 border-l-2 border-primary/40">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold leading-snug">{claim}</div>
          <div className="flex items-center gap-3 mt-1.5 text-[11px]">
            <span className={`font-mono ${confColor}`}>
              {(confidence * 100).toFixed(0)}% confidence
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">Horizon: {horizon} days</span>
            {involved.length > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground truncate">
                  Involves: {involved.slice(0, 4).join(", ")}
                  {involved.length > 4 && ` +${involved.length - 4}`}
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="pl-7 space-y-3 text-[12px]">
          {reasoning && (
            <div>
              <div className="label-eyebrow !mb-1">Reasoning</div>
              <div className="text-foreground/90 leading-relaxed">{reasoning}</div>
            </div>
          )}
          {falsification && (
            <div>
              <div className="label-eyebrow !mb-1">Would be wrong if</div>
              <div className="text-foreground/85 italic leading-relaxed">{falsification}</div>
            </div>
          )}
          {novelty && (
            <div>
              <div className="label-eyebrow !mb-1">Why this isn't obvious</div>
              <div className="text-foreground/85 leading-relaxed">{novelty}</div>
            </div>
          )}
          {(evidenceSignals.length > 0 || evidenceEntities.length > 0) && (
            <div>
              <div className="label-eyebrow !mb-1">Evidence</div>
              <div className="flex flex-wrap gap-1.5">
                {evidenceEntities.map((eid) => (
                  <button
                    key={eid}
                    onClick={() =>
                      navigate({
                        to: ".",
                        search: (prev: Record<string, unknown>) => ({
                          ...prev,
                          entity: eid,
                        }),
                      })
                    }
                    className="rounded border border-border bg-surface-elevated/60 px-2 py-0.5 text-[11px] hover:border-primary/40 hover:text-primary"
                  >
                    {entityNameById[eid] ?? eid.slice(0, 8)}
                  </button>
                ))}
                {evidenceSignals.slice(0, 6).map((sid) => (
                  <span
                    key={sid}
                    className="rounded border border-border/60 bg-surface-elevated/40 px-2 py-0.5 text-[10px] font-mono text-muted-foreground"
                    title={sid}
                  >
                    sig:{sid.slice(0, 6)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-[10px] text-muted-foreground font-mono">
              Generated {new Date(signal.timestamp).toLocaleString()}
            </div>
            {promoted ? (
              <button
                onClick={() =>
                  navigate({
                    to: "/$workspace/scenario-tree",
                    params: { workspace: workspaceId },
                  })
                }
                className="inline-flex items-center gap-1.5 rounded bg-risk-low/15 text-risk-low border border-risk-low/40 px-3 py-1.5 text-[12px] font-semibold"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Scenario created · Open
              </button>
            ) : (
              <button
                onClick={() => promoteMut.mutate()}
                disabled={promoteMut.isPending}
                className="inline-flex items-center gap-1.5 rounded bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-60"
              >
                <GitBranch className="h-3.5 w-3.5" />
                {promoteMut.isPending ? "Promoting…" : "Promote to scenario"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RejectedAttempts({ attempts }: { attempts: { id: string; generated_at: string; hypothesis: Record<string, unknown>; rejection_reasons: string[] }[] }) {
  const [open, setOpen] = useState(false);
  if (attempts.length === 0) {
    return (
      <div className="panel p-4 text-[12px] text-muted-foreground italic">
        No rejected hypotheses yet — the validation gates have not fired in this workspace.
      </div>
    );
  }
  return (
    <div className="panel overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full px-4 py-3 text-[12px] hover:bg-surface-elevated"
      >
        <div className="text-foreground/85">
          {attempts.length} hypothesis attempt{attempts.length === 1 ? "" : "s"} rejected by validation gates
        </div>
        <span className="text-muted-foreground">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-border max-h-[500px] overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-surface-elevated/40">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Claim attempted</th>
                <th className="text-left px-4 py-2">Failed gates</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => {
                const claim = String((a.hypothesis as Record<string, unknown>).claim ?? "");
                return (
                  <tr key={a.id} className="border-t border-border align-top">
                    <td className="px-4 py-2 text-muted-foreground font-mono whitespace-nowrap">
                      {new Date(a.generated_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-foreground/85">
                      {claim || (
                        <span className="text-muted-foreground italic">no claim</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {a.rejection_reasons.map((r) => (
                          <span
                            key={r}
                            className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-mono bg-risk-high/10 text-risk-high border border-risk-high/30"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
